import React, { useState, useEffect } from 'react';
import { instancesApi, reportsApi, getErrorMessage } from '../../services/api';
import LoadingSpinner from '../ui/LoadingSpinner';
import AlertMessage from '../ui/AlertMessage';

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('sv-SE', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
}

function formatYmd(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('sv-SE');
}

function rangeLabel(inst) {
  const date = (inst.startDate || '').slice(0, 10);
  const name = inst.course?.name || 'Okänd kurs';
  const city = inst.city || '';
  return `${date} — ${name}${city ? ' (' + city + ')' : ''}`;
}

function PreCourseReport() {
  const [instances, setInstances] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [report, setReport] = useState(null);
  const [loadingInstances, setLoadingInstances] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    instancesApi.getAll()
      .then(res => {
        const all = res.data.data || [];
        // Sort: upcoming first (closest start date), then past (most recent first)
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const upcoming = all
          .filter(i => i.startDate && new Date(i.startDate) >= today)
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
        const past = all
          .filter(i => i.startDate && new Date(i.startDate) < today)
          .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
          .slice(0, 20);
        const sorted = [...upcoming, ...past];
        setInstances(sorted);
        if (sorted.length > 0) setSelectedId(String(sorted[0].id));
      })
      .catch(err => setError(getErrorMessage(err)))
      .finally(() => setLoadingInstances(false));
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    setLoadingReport(true);
    setError('');
    reportsApi.getPreCourse(selectedId)
      .then(res => setReport(res.data.data))
      .catch(err => {
        setError(getErrorMessage(err));
        setReport(null);
      })
      .finally(() => setLoadingReport(false));
  }, [selectedId]);

  if (loadingInstances) return <LoadingSpinner message="Laddar kursinstanser..." />;

  return (
    <div className="report-content">
      <div className="date-filter" style={{ alignItems: 'center' }}>
        <label style={{ flex: 1 }}>
          Kursinstans:
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            style={{ width: '100%', padding: '8px', marginTop: '4px' }}
          >
            {instances.map(i => (
              <option key={i.id} value={i.id}>{rangeLabel(i)}</option>
            ))}
          </select>
        </label>
      </div>

      <AlertMessage error={error} />

      {loadingReport && <LoadingSpinner message="Hämtar förkurs-svar..." />}

      {!loadingReport && report && (
        <>
          {/* Course meta */}
          <div className="report-section" style={{ background: '#f0f4f8', padding: '15px 20px', borderRadius: '6px' }}>
            <h3 style={{ margin: '0 0 10px', color: '#0c4da2' }}>{report.instance.courseName}</h3>
            <div><strong>Datum:</strong> {formatDate(report.instance.startDate)}</div>
            <div><strong>Plats:</strong> {report.instance.city || '-'}{report.instance.location ? ' — ' + report.instance.location : ''}</div>
            <div>
              <strong>Kursledare:</strong>{' '}
              {report.teachers.primary
                ? `${report.teachers.primary.name || ''} <${report.teachers.primary.email}>`
                : '-'}
            </div>
            {report.teachers.assistants.length > 0 && (
              <div>
                <strong>Hjälplärare:</strong>{' '}
                {report.teachers.assistants.map(a => `${a.name || ''} <${a.email}>`).join(', ')}
              </div>
            )}
            <div style={{ marginTop: '8px' }}>
              <strong>Antal bokningar:</strong> {report.summary.total}
              {' '}({report.summary.confirmed} bekräftade, {report.summary.waitlist} väntelista)
              {report.summary.notCompletedCount > 0 && (
                <span style={{ color: '#c00', marginLeft: '12px' }}>
                  · {report.summary.notCompletedCount} ej kompletterat
                </span>
              )}
            </div>
          </div>

          {/* Dietary block - first */}
          <div className="report-section" style={{ background: '#fff3cd', borderLeft: '4px solid #ffc107', padding: '15px 20px', borderRadius: '4px' }}>
            <h3 style={{ margin: '0 0 8px', color: '#856404' }}>🍽️ Allergier &amp; specialkost</h3>
            {report.summary.dietary.length === 0 ? (
              <p style={{ margin: 0, color: '#666', fontStyle: 'italic' }}>
                Inga deltagare har angett allergier eller specialkost.
              </p>
            ) : (
              <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                {report.summary.dietary.map((d, i) => (
                  <li key={i} style={{ margin: '4px 0' }}>
                    <strong>{d.name}:</strong> {d.detail}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Participants table */}
          <div className="report-section">
            <h3>Förkurs-svar per deltagare</h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="report-table" style={{ fontSize: '0.9rem' }}>
                <thead>
                  <tr>
                    <th>Deltagare</th>
                    <th>Företag</th>
                    <th>Specialkost</th>
                    <th>AI-erfarenhet</th>
                    <th>AI-verktyg</th>
                    <th>Förväntningar</th>
                    <th>Teams</th>
                    <th>Komplettering</th>
                  </tr>
                </thead>
                <tbody>
                  {report.participants.map(p => {
                    const teams = p.teamsEmailOk === true ? 'Ja' : p.teamsEmailOk === false ? 'Nej' : '-';
                    const completedLabel = p.completionCompletedAt
                      ? `Klar ${formatYmd(p.completionCompletedAt)}`
                      : 'EJ KOMPLETTERAT';
                    const completedColor = p.completionCompletedAt ? '#2a7' : '#c00';
                    return (
                      <tr key={p.bookingId}>
                        <td>
                          <div>{p.firstName} {p.lastName}</div>
                          <small style={{ color: '#666' }}>{p.email}</small>
                        </td>
                        <td>{p.company || '-'}</td>
                        <td><strong>{p.specialDiet || '-'}</strong></td>
                        <td>{p.aiExperienceLabel || '-'}</td>
                        <td>{p.aiToolsLabel || '-'}</td>
                        <td style={{ maxWidth: '280px' }}>{p.expectations || '-'}</td>
                        <td>{teams}</td>
                        <td style={{ color: completedColor, fontWeight: p.completionCompletedAt ? 'normal' : 600 }}>
                          {completedLabel}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PreCourseReport;
